import React, { useState, useEffect, useCallback } from 'react';
import {
  Table,
  Button,
  Drawer,
  Form,
  Input,
  Switch,
  Space,
  message,
  Popconfirm,
  Tag,
} from 'antd';
import { PlusOutlined, ReloadOutlined } from '@ant-design/icons';
import { useAuth } from '../../contexts/AuthContext';
import { PERMISSIONS } from '../../config/permissions';
import DashboardPageWrapper from '../../components/layout/DashboardPageWrapper';
import businessTeamAlertService from '../../services/businessTeamAlertService';

export default function BusinessTeamAlertsPage() {
  const { hasPermission, isSuperAdmin } = useAuth();
  const canCreate = isSuperAdmin || hasPermission(PERMISSIONS.PRICING_BUSINESS_ALERTS_CREATE);
  const canEdit = isSuperAdmin || hasPermission(PERMISSIONS.PRICING_BUSINESS_ALERTS_EDIT);

  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form] = Form.useForm();

  const loadAlerts = useCallback(async () => {
    setLoading(true);
    try {
      const list = await businessTeamAlertService.list();
      setAlerts(list);
    } catch (e) {
      message.error(e?.message || 'Failed to load business team alerts');
      setAlerts([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAlerts();
  }, [loadAlerts]);

  const openCreate = () => {
    form.resetFields();
    form.setFieldsValue({ is_active: true });
    setDrawerOpen(true);
  };

  const onSubmit = async () => {
    try {
      const values = await form.validateFields();
      setSubmitting(true);
      const res = await businessTeamAlertService.create({
        email: values.email.trim(),
        name: values.name?.trim() || null,
        phone_number: values.phone_number?.trim() || null,
        is_active: values.is_active,
      });
      if (res?.success === false) {
        message.error(res?.message || 'Create failed');
        return;
      }
      message.success('Alert recipient added');
      setDrawerOpen(false);
      loadAlerts();
    } catch (e) {
      if (e?.errorFields) return;
      message.error(e?.response?.data?.message || e?.message || 'Save failed');
    } finally {
      setSubmitting(false);
    }
  };

  const toggleActive = async (record) => {
    try {
      const res = await businessTeamAlertService.update(record.id, {
        is_active: !record.is_active,
      });
      if (res?.success === false) {
        message.error(res?.message || 'Update failed');
        return;
      }
      loadAlerts();
    } catch (e) {
      message.error(e?.response?.data?.message || e?.message || 'Update failed');
    }
  };

  const onDelete = async (record) => {
    try {
      const res = await businessTeamAlertService.remove(record.id);
      if (res?.success === false) {
        message.error(res?.message || 'Delete failed');
        return;
      }
      message.success('Recipient removed');
      loadAlerts();
    } catch (e) {
      message.error(e?.response?.data?.message || e?.message || 'Delete failed');
    }
  };

  const columns = [
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      render: (text) => <span className="font-medium text-gray-800">{text}</span>,
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'Phone',
      dataIndex: 'phone_number',
      key: 'phone_number',
    },
    {
      title: 'Active',
      dataIndex: 'is_active',
      key: 'is_active',
      width: 90,
      render: (v) => <Tag color={v ? 'green' : 'default'}>{v ? 'Yes' : 'No'}</Tag>,
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 180,
      render: (_, record) => (
        <Space size="small">
          {canEdit && (
            <Button size="small" onClick={() => toggleActive(record)}>
              {record.is_active ? 'Deactivate' : 'Activate'}
            </Button>
          )}
          {canEdit && (
            <Popconfirm title="Remove this recipient?" onConfirm={() => onDelete(record)}>
              <Button size="small" danger>
                Delete
              </Button>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  return (
    <DashboardPageWrapper>
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Business team alerts</h1>
          <p className="text-sm text-gray-500 mt-1">
            Email recipients for morning deck review (Approve / Modify list).
          </p>
        </div>
        {canCreate && (
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
            Add recipient
          </Button>
        )}
      </div>

      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        <div className="p-4 border-b border-gray-100 flex justify-end bg-gray-50/50">
          <Button icon={<ReloadOutlined />} onClick={loadAlerts} loading={loading}>
            Refresh
          </Button>
        </div>
        <Table
          columns={columns}
          dataSource={alerts}
          rowKey={(r) => r.id}
          loading={loading}
          pagination={false}
        />
      </div>

      <Drawer
        title="Add deck alert recipient"
        width={420}
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        extra={
          <Space>
            <Button onClick={() => setDrawerOpen(false)}>Cancel</Button>
            <Button type="primary" loading={submitting} onClick={onSubmit}>
              Save
            </Button>
          </Space>
        }
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="email"
            label="Email"
            rules={[{ required: true, type: 'email', message: 'Enter a valid email' }]}
          >
            <Input />
          </Form.Item>
          <Form.Item name="name" label="Name">
            <Input />
          </Form.Item>
          <Form.Item name="phone_number" label="Phone">
            <Input />
          </Form.Item>
          <Form.Item name="is_active" label="Active" valuePropName="checked">
            <Switch checkedChildren="Yes" unCheckedChildren="No" />
          </Form.Item>
        </Form>
      </Drawer>
    </DashboardPageWrapper>
  );
}
